import { type CSSProperties, type ReactNode } from 'react';
import { useOpenLightbox } from './Lightbox';
import {
  IconAlertCircleFilled,
  IconArrowBackUp,
  IconCheck,
  IconChecks,
  IconChevronRight,
  IconCopy,
  IconDownload,
  IconExternalLink,
  IconFileText,
  IconMapPin,
  IconPhone,
  IconPhoto,
  IconPhotoOff,
} from '@tabler/icons-react';

/**
 * Native-Meta chat bubbles for the LeadSync inbox.
 *
 * Renders each channel's real look — WhatsApp (green on beige, with a tail),
 * Messenger (blue pills, grouped), Instagram (gradient, very round) — plus a
 * distinct layout per WhatsApp/Meta template `type`:
 *   text · image · video · audio · file · buttons · quickReplies · cards ·
 *   receipt · attachment.
 *
 * Self-contained: colors use CSS `light-dark()` so it follows the Mantine
 * color scheme with no extra tokens. Rendered from the inbox MessageBubble
 * shell and the template-builder live preview (see HANDOFF.md for the mapping).
 */

/* ─────────────────────────── types ─────────────────────────── */
export type Channel = 'whatsapp' | 'messenger' | 'instagram';
export type BubbleType =
  | 'text'
  | 'image'
  | 'video'
  | 'audio'
  | 'file'
  | 'sticker'
  | 'location'
  | 'buttons'
  | 'quickReplies'
  | 'cards'
  | 'receipt'
  | 'attachment';
export type DeliveryStatus = 'sent' | 'delivered' | 'read' | 'failed';
export type Seq = 'single' | 'first' | 'mid' | 'last';

export interface BubbleBtn {
  title: string;
  kind?: 'url' | 'phone' | 'reply' | 'copy';
}
export interface BubbleCardData {
  imageUrl?: string;
  title?: string;
  subtitle?: string;
  buttons?: BubbleBtn[];
}
export interface ReceiptItem {
  title: string;
  qty?: number;
  price: number | string;
}
export interface ReceiptData {
  orderNumber?: string;
  name?: string;
  currency?: string;
  items?: ReceiptItem[];
  total?: number | string;
  paid?: string;
}
export interface AttachmentData {
  kind?: string;
  label?: string;
  imageUrl?: string;
  avatar?: string;
}
export interface MediaData {
  url?: string;
  width?: number;
  height?: number;
  name?: string;
  size?: string;
  duration?: string;
  mimeType?: string;
  lat?: number;
  long?: number;
}

export interface MessageBubbleProps {
  text?: ReactNode;
  children?: ReactNode;
  type?: BubbleType;
  incoming?: boolean;
  channel?: Channel;
  time?: string;
  status?: DeliveryStatus;
  seq?: Seq;
  replyTo?: ReactNode | { author: string; text: string };
  /** Ad/referral provenance chip (Click-to-Messenger / m.me). */
  referral?: { headline?: string | null } | null;
  media?: MediaData;
  buttons?: BubbleBtn[];
  quickReplies?: string[];
  cards?: BubbleCardData[];
  receipt?: ReceiptData;
  attachment?: AttachmentData;
  reaction?: string;
  style?: CSSProperties;
}

/** The body-only subset a channel mapper / builder preview produces (the shell
 *  supplies channel/incoming/seq/time/status). */
export type BubbleBodyProps = Pick<
  MessageBubbleProps,
  | 'type'
  | 'text'
  | 'children'
  | 'media'
  | 'buttons'
  | 'quickReplies'
  | 'cards'
  | 'receipt'
  | 'attachment'
  | 'reaction'
>;

/* ─────────────────────── channel theme ─────────────────────── */
interface ChannelTheme {
  out: { bg: string; fg: string };
  in: { bg: string; fg: string };
  link: string;
  meta: string;
  tickRead: string;
  radius: number;
  small: number;
  tail: boolean;
  inlineMeta: boolean;
}

const LD = (l: string, d: string) => `light-dark(${l}, ${d})`;

export const CHANNEL_THEME: Record<Channel, ChannelTheme> = {
  whatsapp: {
    out: { bg: LD('#d9fdd3', '#005c4b'), fg: LD('#111b21', '#e9edef') },
    in: { bg: LD('#ffffff', '#202c33'), fg: LD('#111b21', '#e9edef') },
    link: LD('#027eb5', '#53bdeb'),
    meta: LD('#667781', '#8696a0'),
    tickRead: '#53bdeb',
    radius: 8,
    small: 4,
    tail: true,
    inlineMeta: true,
  },
  messenger: {
    out: { bg: '#0084ff', fg: '#fff' },
    in: { bg: LD('#f0f0f0', '#3a3b3c'), fg: LD('#050505', '#e4e6eb') },
    link: LD('#0084ff', '#2997ff'),
    meta: LD('#65676b', '#b0b3b8'),
    tickRead: '#0084ff',
    radius: 18,
    small: 5,
    tail: false,
    inlineMeta: false,
  },
  instagram: {
    out: {
      bg: 'linear-gradient(95deg, #5b51d8 0%, #a02fbf 48%, #cd3066 100%)',
      fg: '#fff',
    },
    in: { bg: LD('#efefef', '#262626'), fg: LD('#000000', '#ffffff') },
    link: '#0095f6',
    meta: LD('#8e8e8e', '#a8a8a8'),
    tickRead: '#0095f6',
    radius: 22,
    small: 6,
    tail: false,
    inlineMeta: false,
  },
};

/** Thread background for each channel's scroll area. */
export const THREAD_BG: Record<Channel, string> = {
  whatsapp: LD('#efeae2', '#0b141a'),
  messenger: LD('#ffffff', '#18191a'),
  instagram: LD('#ffffff', '#000000'),
};

function corners(t: ChannelTheme, incoming: boolean, seq: Seq): string {
  const R = t.radius,
    s = t.small;
  let tl = R,
    tr = R,
    br = R,
    bl = R;
  if (incoming) {
    if (seq === 'first') bl = s;
    else if (seq === 'mid') {
      tl = s;
      bl = s;
    } else if (seq === 'last') tl = s;
  } else {
    if (seq === 'first') br = s;
    else if (seq === 'mid') {
      tr = s;
      br = s;
    } else if (seq === 'last') tr = s;
  }
  if (t.tail && (seq === 'single' || seq === 'first')) {
    if (incoming) tl = 0;
    else tr = 0;
  }
  return `${tl}px ${tr}px ${br}px ${bl}px`;
}

function Ticks({ status, color, read }: { status: DeliveryStatus; color: string; read: string }) {
  if (status === 'failed') return <IconAlertCircleFilled size={14} color="#fa5252" />;
  if (status === 'sent') return <IconCheck size={14} color={color} style={{ opacity: 0.8 }} />;
  if (status === 'read') return <IconChecks size={14} color={read} />;
  return <IconChecks size={14} color={color} style={{ opacity: 0.85 }} />;
}

/* ─────────────────────── main component ─────────────────────── */
export function MessageBubble({
  children,
  text,
  type = 'text',
  incoming = false,
  channel = 'whatsapp',
  time,
  status = 'read',
  seq = 'single',
  replyTo,
  referral,
  media,
  buttons,
  quickReplies,
  cards,
  receipt,
  attachment,
  reaction,
  style,
}: MessageBubbleProps) {
  const t = CHANNEL_THEME[channel] ?? CHANNEL_THEME.whatsapp;
  const tone = incoming ? t.in : t.out;
  const body = text ?? children;
  const isRich = type === 'cards' || type === 'receipt' || type === 'attachment';
  const isSticker = type === 'sticker';
  const isMedia = type === 'image' || type === 'video' || type === 'audio' || type === 'file';
  const pad = isSticker ? 0 : isMedia && !body ? 4 : '6px 9px 5px';

  const bubbleBg = isSticker ? 'transparent' : isRich ? LD('#ffffff', '#242424') : tone.bg;
  const bubbleFg = isRich ? LD('#111b21', '#e9edef') : tone.fg;
  const metaColor = incoming ? t.meta : channel === 'whatsapp' ? t.meta : 'rgba(255,255,255,0.9)';

  const metaInline = (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        float: t.inlineMeta ? 'right' : 'none',
        marginLeft: t.inlineMeta ? 8 : 0,
        marginTop: t.inlineMeta ? 5 : 0,
        position: 'relative',
        top: t.inlineMeta ? 4 : 0,
      }}
    >
      {time && <span style={{ fontSize: 11, color: metaColor }}>{time}</span>}
      {!incoming && <Ticks status={status} color={metaColor} read={t.tickRead} />}
    </span>
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: incoming ? 'flex-start' : 'flex-end',
        gap: 2,
        ...style,
      }}
    >
      <div style={{ position: 'relative', maxWidth: isRich ? 264 : '75%' }}>
        {t.tail && (seq === 'single' || seq === 'first') && (
          <span
            style={
              {
                position: 'absolute',
                top: 0,
                width: 8,
                height: 13,
                overflow: 'hidden',
                [incoming ? 'left' : 'right']: -7,
              } as CSSProperties
            }
          >
            <span
              style={
                {
                  position: 'absolute',
                  top: 0,
                  [incoming ? 'right' : 'left']: 0,
                  width: 13,
                  height: 13,
                  background: tone.bg,
                  clipPath: incoming
                    ? 'polygon(100% 0, 0 0, 100% 100%)'
                    : 'polygon(0 0, 100% 0, 0 100%)',
                } as CSSProperties
              }
            />
          </span>
        )}
        <div
          style={{
            background: bubbleBg,
            color: bubbleFg,
            borderRadius: corners(t, incoming, seq),
            padding: isRich ? 0 : pad,
            overflow: 'hidden',
            border: isSticker
              ? 'none'
              : incoming && channel === 'whatsapp' && !isRich
                ? '1px solid rgba(0,0,0,0.04)'
                : isRich
                  ? '1px solid light-dark(#dee2e6,#424242)'
                  : 'none',
            boxShadow:
              !isSticker && channel === 'whatsapp' && !isRich
                ? '0 1px 0.5px rgba(11,20,26,0.13)'
                : 'none',
            fontSize: 14,
            lineHeight: 1.4,
            wordBreak: 'break-word',
          }}
        >
          {replyTo != null && (
            <ReplyQuote replyTo={replyTo} link={t.link} incoming={incoming} rich={isRich} />
          )}
          {referral?.headline && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 11,
                opacity: 0.85,
                marginBottom: 4,
              }}
            >
              <IconExternalLink size={12} />
              <span>From ad: {referral.headline}</span>
            </div>
          )}

          {isMedia && <MediaBody type={type} media={media ?? {}} fg={bubbleFg} />}
          {isSticker && <StickerBody media={media ?? {}} />}
          {type === 'location' && <LocationBody media={media ?? {}} fg={bubbleFg} />}

          {body != null && body !== '' && (
            <div style={{ padding: isMedia ? '2px 6px 4px' : 0, whiteSpace: 'pre-wrap' }}>
              {body}
              {t.inlineMeta && !isMedia && metaInline}
            </div>
          )}

          {type === 'buttons' && (
            <ButtonList
              buttons={buttons ?? []}
              link={t.link}
              incoming={incoming}
              channel={channel}
            />
          )}
          {type === 'cards' && <CardBody cards={cards ?? []} link={t.link} />}
          {type === 'receipt' && <ReceiptBody receipt={receipt ?? {}} />}
          {type === 'attachment' && <AttachmentBody attachment={attachment ?? {}} />}

          {type === 'image' && !body && (
            <div
              style={{
                position: 'absolute',
                right: 8,
                bottom: 7,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
                padding: '1px 6px',
                borderRadius: 8,
                background: 'rgba(0,0,0,0.4)',
              }}
            >
              {time && <span style={{ fontSize: 11, color: '#fff' }}>{time}</span>}
              {!incoming && <Ticks status={status} color="rgba(255,255,255,0.9)" read="#7ad1ff" />}
            </div>
          )}

          {!t.inlineMeta && !isRich && type !== 'image' && (time || !incoming) && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
              {metaInline}
            </div>
          )}
        </div>
      </div>

      {type === 'quickReplies' && (quickReplies?.length ?? 0) > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            justifyContent: incoming ? 'flex-start' : 'flex-end',
            marginTop: 3,
          }}
        >
          {quickReplies!.map((q, i) => (
            <span
              key={i}
              style={{
                padding: '6px 14px',
                borderRadius: 999,
                fontSize: 13,
                fontWeight: 500,
                background: LD('#ffffff', '#242424'),
                color: t.link,
                border: `1px solid ${channel === 'whatsapp' ? 'rgba(0,0,0,0.12)' : 'light-dark(#dee2e6,#424242)'}`,
              }}
            >
              {q}
            </span>
          ))}
        </div>
      )}

      {reaction && (
        <span
          style={{
            fontSize: 13,
            background: LD('#ffffff', '#242424'),
            border: '1px solid light-dark(#e9ecef,#3b3b3b)',
            borderRadius: 999,
            padding: '1px 7px',
            marginTop: -8,
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          }}
        >
          {reaction}
        </span>
      )}

      {!t.inlineMeta && !incoming && (seq === 'single' || seq === 'last') && (
        <span style={{ fontSize: 11, color: t.meta, marginTop: 1 }}>
          {status === 'read'
            ? 'Seen'
            : status === 'delivered'
              ? 'Delivered'
              : status === 'failed'
                ? 'Not delivered'
                : 'Sent'}
        </span>
      )}
    </div>
  );
}

/* ─────────────────────── sub-renderers ─────────────────────── */
function ReplyQuote({
  replyTo,
  link,
  incoming,
  rich,
}: {
  replyTo: NonNullable<MessageBubbleProps['replyTo']>;
  link: string;
  incoming: boolean;
  rich: boolean;
}) {
  const obj =
    typeof replyTo === 'object' && replyTo !== null && 'text' in replyTo
      ? (replyTo as { author: string; text: string })
      : null;
  return (
    <div
      style={{
        borderLeft: `3px solid ${link}`,
        padding: '3px 8px',
        margin: rich ? 8 : '0 0 4px',
        borderRadius: 4,
        background: incoming ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.18)',
        fontSize: 12,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {obj ? (
        <>
          <div style={{ fontWeight: 600, color: link, marginBottom: 1 }}>{obj.author}</div>
          <div style={{ opacity: 0.85 }}>{obj.text}</div>
        </>
      ) : (
        <span style={{ opacity: 0.85 }}>{replyTo as ReactNode}</span>
      )}
    </div>
  );
}

function ratioOf(media: MediaData): number {
  return media.height && media.width ? media.height / media.width : 0.66;
}

/** Inline "couldn't load" chip for a null / expired media url. */
function MediaUnavailable({ label, fg }: { label: string; fg: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 2px',
        color: fg,
        opacity: 0.7,
      }}
    >
      <IconPhotoOff size={16} />
      <span style={{ fontSize: 12 }}>{label} unavailable</span>
    </div>
  );
}

/** Fixed-ratio grey box while a visual has no url yet (no layout shift). */
function MediaPlaceholder({ ratio }: { ratio: number }) {
  return (
    <div
      style={{
        width: 250,
        maxWidth: '100%',
        borderRadius: 6,
        overflow: 'hidden',
        background: '#dee2e6',
      }}
    >
      <div
        style={{
          width: '100%',
          paddingBottom: `${ratio * 100}%`,
          background: 'linear-gradient(135deg, #e9ecef, #ced4da)',
        }}
      />
    </div>
  );
}

function MediaBody({ type, media, fg }: { type: BubbleType; media: MediaData; fg: string }) {
  if (type === 'audio') {
    return media.url ? (
      <audio
        src={media.url}
        controls
        preload="metadata"
        style={{ display: 'block', width: '100%', minWidth: 220, maxWidth: 260, height: 36 }}
      />
    ) : (
      <MediaUnavailable label="Audio" fg={fg} />
    );
  }
  if (type === 'video') {
    return media.url ? (
      <video
        src={media.url}
        controls
        preload="metadata"
        style={{
          display: 'block',
          width: 250,
          maxWidth: '100%',
          maxHeight: 320,
          borderRadius: 6,
          background: '#000',
        }}
      />
    ) : (
      <MediaPlaceholder ratio={ratioOf(media)} />
    );
  }
  if (type === 'file') {
    return (
      <a
        href={media.url || undefined}
        target="_blank"
        rel="noreferrer"
        download
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 4px',
          minWidth: 200,
          color: 'inherit',
          textDecoration: 'none',
          pointerEvents: media.url ? 'auto' : 'none',
        }}
      >
        <span
          style={{
            width: 38,
            height: 38,
            borderRadius: 8,
            background: 'rgba(0,0,0,0.12)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <IconFileText size={20} color={fg} />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {media.name ?? 'Attachment'}
          </div>
          <div style={{ fontSize: 11, opacity: 0.7 }}>
            {media.size ?? media.mimeType ?? 'Download'}
          </div>
        </div>
        <IconDownload
          size={16}
          color={fg}
          style={{ marginLeft: 'auto', opacity: 0.8, flexShrink: 0 }}
        />
      </a>
    );
  }
  // image / gif — `contain` (never crop); an <img> animates gifs natively.
  // Click to open full-size (parity with the WhatsApp inbox).
  return media.url ? (
    <ZoomableImage url={media.url} />
  ) : (
    <MediaPlaceholder ratio={ratioOf(media)} />
  );
}

/** Image bubble — click opens the shared lightbox (close button + ‹ › nav). */
function ZoomableImage({ url }: { url: string }) {
  const openLightbox = useOpenLightbox();
  return (
    <div
      style={{
        width: 250,
        maxWidth: '100%',
        borderRadius: 6,
        overflow: 'hidden',
        background: '#dee2e6',
      }}
    >
      <img
        src={url}
        alt=""
        loading="lazy"
        data-lb-src={url}
        onClick={() => openLightbox(url)}
        style={{
          display: 'block',
          width: '100%',
          height: 'auto',
          maxHeight: 320,
          objectFit: 'contain',
          cursor: 'pointer',
        }}
      />
    </div>
  );
}

/** Sticker / 👍 like — small, transparent, never cropped (no bubble chrome). */
function StickerBody({ media }: { media: MediaData }) {
  if (!media.url) return null;
  return (
    <img
      src={media.url}
      alt="sticker"
      loading="lazy"
      style={{ display: 'block', width: 124, maxWidth: 132, height: 'auto', objectFit: 'contain' }}
    />
  );
}

/** Location pin — a tappable row that opens the coordinates in Google Maps. */
function LocationBody({ media, fg }: { media: MediaData; fg: string }) {
  const { lat, long } = media;
  const href =
    lat != null && long != null
      ? `https://www.google.com/maps/search/?api=1&query=${lat},${long}`
      : (media.url ?? undefined);
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '4px 2px',
        minWidth: 180,
        color: 'inherit',
        textDecoration: 'none',
      }}
    >
      <IconMapPin size={20} color={fg} style={{ flexShrink: 0 }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontWeight: 500 }}>Location</div>
        {lat != null && long != null && (
          <div style={{ fontSize: 11, opacity: 0.7 }}>
            {lat.toFixed(5)}, {long.toFixed(5)}
          </div>
        )}
      </div>
      <IconExternalLink
        size={15}
        color={fg}
        style={{ marginLeft: 'auto', opacity: 0.7, flexShrink: 0 }}
      />
    </a>
  );
}

function ButtonList({
  buttons,
  link,
  incoming,
  channel,
}: {
  buttons: BubbleBtn[];
  link: string;
  incoming: boolean;
  channel: Channel;
}) {
  if (!buttons.length) return null;
  const Icon = (k?: BubbleBtn['kind']) =>
    k === 'phone'
      ? IconPhone
      : k === 'reply'
        ? IconArrowBackUp
        : k === 'copy'
          ? IconCopy
          : IconExternalLink;
  const divider = incoming ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.25)';
  return (
    <div
      style={{
        marginTop: 6,
        marginInline: -9,
        marginBottom: -5,
        borderTop: `1px solid ${divider}`,
      }}
    >
      {buttons.map((b, i) => {
        const I = Icon(b.kind);
        return (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              padding: '9px 8px',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              color: incoming || channel === 'whatsapp' ? link : '#fff',
              borderTop: i ? `1px solid ${divider}` : 'none',
            }}
          >
            <I size={15} />
            {b.title}
          </div>
        );
      })}
    </div>
  );
}

function CardBody({ cards, link }: { cards: BubbleCardData[]; link: string }) {
  const single = cards.length <= 1;
  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        overflowX: single ? 'visible' : 'auto',
        scrollSnapType: 'x mandatory',
      }}
    >
      {cards.map((c, i) => (
        <div key={i} style={{ flex: single ? '1 1 auto' : '0 0 232px', scrollSnapAlign: 'start' }}>
          <CardItem card={c} link={link} />
        </div>
      ))}
    </div>
  );
}

function CardItem({ card, link }: { card: BubbleCardData; link: string }) {
  return (
    <div>
      {card.imageUrl ? (
        <img
          src={card.imageUrl}
          alt=""
          style={{ width: '100%', height: 130, objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <div
          style={{
            width: '100%',
            height: 130,
            background: 'linear-gradient(135deg, #e9ecef, #ced4da)',
          }}
        />
      )}
      <div style={{ padding: 10 }}>
        {card.title && <div style={{ fontWeight: 600, fontSize: 14 }}>{card.title}</div>}
        {card.subtitle && (
          <div style={{ fontSize: 12, color: LD('#868e96', '#828282'), marginTop: 2 }}>
            {card.subtitle}
          </div>
        )}
      </div>
      {(card.buttons?.length ?? 0) > 0 && (
        <div style={{ borderTop: '1px solid light-dark(#e9ecef,#3b3b3b)' }}>
          {card.buttons!.map((b, i) => (
            <div
              key={i}
              style={{
                padding: 8,
                textAlign: 'center',
                fontSize: 13,
                fontWeight: 500,
                color: link,
                cursor: 'pointer',
                borderTop: i ? '1px solid light-dark(#e9ecef,#3b3b3b)' : 'none',
              }}
            >
              {b.title}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReceiptBody({ receipt }: { receipt: ReceiptData }) {
  const items = receipt.items ?? [];
  const cur = receipt.currency ?? '₹';
  return (
    <div style={{ padding: 12 }}>
      <div
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: LD('#868e96', '#828282'),
          fontWeight: 600,
        }}
      >
        Receipt
      </div>
      {receipt.orderNumber && <div style={{ fontWeight: 600 }}>Order {receipt.orderNumber}</div>}
      {receipt.name && (
        <div style={{ fontSize: 12, color: LD('#868e96', '#828282') }}>{receipt.name}</div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 8 }}>
        {items.map((it, i) => (
          <div
            key={i}
            style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, gap: 8 }}
          >
            <span>
              {it.title}
              {it.qty ? ` × ${it.qty}` : ''}
            </span>
            <span>
              {cur} {it.price}
            </span>
          </div>
        ))}
      </div>
      {receipt.total != null && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 8,
            paddingTop: 6,
            borderTop: '1px solid light-dark(#dee2e6,#424242)',
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          <span>Total</span>
          <span>
            {cur} {receipt.total}
          </span>
        </div>
      )}
      {receipt.paid && (
        <div style={{ fontSize: 12, color: '#2f9e44', marginTop: 4 }}>Paid · {receipt.paid}</div>
      )}
    </div>
  );
}

function AttachmentBody({ attachment }: { attachment: AttachmentData }) {
  return (
    <div>
      {attachment.imageUrl ? (
        <img
          src={attachment.imageUrl}
          alt=""
          style={{ width: '100%', height: 150, objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <div
          style={{
            width: '100%',
            height: 150,
            background: 'linear-gradient(135deg, #e9ecef, #ced4da)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconPhoto size={28} color="#868e96" />
        </div>
      )}
      <div style={{ padding: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
        {attachment.avatar && (
          <span
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: '#dee2e6',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            {attachment.avatar}
          </span>
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 12, color: LD('#868e96', '#828282') }}>
            {attachment.kind ?? 'Shared a post'}
          </div>
          {attachment.label && (
            <div
              style={{
                fontWeight: 600,
                fontSize: 14,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {attachment.label}
            </div>
          )}
        </div>
        <IconChevronRight size={16} color={LD('#868e96', '#828282')} />
      </div>
    </div>
  );
}

/** Compute `seq` for a list so consecutive same-sender bubbles group + tail. */
export function computeSeq<T extends { incoming?: boolean; isIncoming?: boolean }>(
  messages: T[],
): Seq[] {
  return messages.map((m, i) => {
    const dir = (x?: T) => (x ? (x.incoming ?? x.isIncoming) : undefined);
    const me = dir(m),
      p = dir(messages[i - 1]),
      n = dir(messages[i + 1]);
    const sameP = messages[i - 1] && p === me;
    const sameN = messages[i + 1] && n === me;
    if (sameP && sameN) return 'mid';
    if (sameN) return 'first';
    if (sameP) return 'last';
    return 'single';
  });
}
